/**
 * Ratio and rate computations for 6th-grade standards.
 */
import { Rational, rat } from "./rational";

/** A ratio a:b represented as two Rationals. */
export interface RatioValue {
  readonly a: Rational;
  readonly b: Rational;
}

/** Unit rate: a per 1 unit of b. Returns a/b as a Rational. */
export function unitRate(a: Rational, b: Rational): Rational {
  return a.div(b);
}

/** Check if two ratios are equivalent: a1/b1 = a2/b2 via cross multiplication. */
export function equivalentRatios(
  a1: Rational,
  b1: Rational,
  a2: Rational,
  b2: Rational,
): boolean {
  return a1.mul(b2).equals(a2.mul(b1));
}

/**
 * Common unit conversion factors from the SC reference sheet.
 * All values are exact Rationals.
 */
export const CONVERSIONS = {
  // Length
  feetPerMile: rat(5280),
  feetPerYard: rat(3),
  inchesPerFoot: rat(12),
  cmPerInch: rat(254, 100),
  cmPerMeter: rat(100),
  mmPerCm: rat(10),
  mPerKm: rat(1000),

  // Mass/Weight
  ozPerPound: rat(16),
  gPerKg: rat(1000),

  // Capacity
  cupsPerPint: rat(2),
  pintsPerQuart: rat(2),
  quartsPerGallon: rat(4),
  mlPerLiter: rat(1000),

  // Time
  secondsPerMinute: rat(60),
  minutesPerHour: rat(60),
  hoursPerDay: rat(24),
} as const;

/** Convert a value using a conversion factor. */
export function convert(value: Rational, factor: Rational, direction: "multiply" | "divide"): Rational {
  return direction === "multiply" ? value.mul(factor) : value.div(factor);
}
